import moment, { isMoment } from 'moment';
import { FilterOperator } from '../../../types/gql-types';
import { DateUtils } from '../../../utils/DateUtils';
import { Combiner } from '../components/Combiner';
import { FilterValueInputType } from '../enum/FilterValueInputType';
import { Operator } from '../enum/Operator';
import { Expression, FilterConfiguration } from '../types';
import { expressionValidator } from '../utils';

export class QueryFilterMapper {
  private idTracker: number;

  constructor() {
    this.idTracker = 1;
  }

  // Parsing
  parseQueryValue(rawData: unknown, fieldConfig: FilterConfiguration[]): Combiner | null {
    try {
      if (typeof rawData !== 'object' || !rawData) {
        return null;
      }

      return this.parseOperator(rawData, fieldConfig);
    } catch {
      // if invalid filter map, return null
      return null;
    }
  }

  private parseOperator(data: any, fieldConfig: FilterConfiguration[]): Combiner {
    const { operator, subFilters } = data;

    if (operator === undefined || ![Operator.AND, Operator.OR].includes(operator)) {
      throw new Error('Invalid operator');
    }

    if (!Array.isArray(subFilters)) {
      throw new Error('subFilters needs to be an array.');
    }

    const outputSubFilters: (Combiner | Expression)[] = [];
    for (const subFilter of subFilters) {
      if (typeof subFilter !== 'object' || !subFilter) {
        throw new Error('Invalid subfilter configuration');
      }

      if ('operator' in subFilter) {
        outputSubFilters.push(this.parseOperator(subFilter, fieldConfig));
        continue;
      }

      outputSubFilters.push(this.parseOperation(subFilter, fieldConfig));
    }

    return {
      id: this.getId(),
      operator,
      subFilters: outputSubFilters,
    };
  }

  private parseOperation(data: any, fieldConfig: FilterConfiguration[]): Expression {
    const { field, operation, value } = data;

    // Field validation
    if (typeof field !== 'string') {
      throw new Error('Field is not the right type');
    }

    const fieldObject = this.getField(field, fieldConfig);
    if (fieldObject === null) {
      throw new Error('Non existing field provided.');
    }

    // Validate operation by field
    const availableOperations = fieldObject.operations;
    if (!availableOperations.find(({ value }) => value === operation)) {
      throw new Error('Non existing operation.');
    }

    // Validate value by type
    if (value === undefined) {
      throw new Error('Non existing value.');
    }
    let outputValue = value;
    switch (fieldObject.type) {
      case FilterValueInputType.DATE_TIME:
      case FilterValueInputType.TIMESTAMP: {
        const date = moment(value.replace('+00:00', 'Z'), moment.ISO_8601);
        if (!date.isValid()) {
          throw new Error('Invalid date provided');
        }
        outputValue = date;
        break;
      }
      case FilterValueInputType.SELECT: {
        const option = fieldObject.options.find((option) => option.value === value);
        if (!option) {
          throw new Error('Invalid option selected');
        }
        break;
      }
      default:
      // do nothing;
    }

    const validation = expressionValidator(fieldObject, operation, outputValue);
    if (validation !== true) {
      throw new Error('Filter does not pass validations');
    }

    return {
      id: this.getId(),
      field,
      operation,
      value: outputValue,
    };
  }

  private getId(): number {
    return this.idTracker++;
  }

  // Mapping
  mapCombiner(combiner: Combiner): FilterOperator {
    return {
      operator: combiner.operator,
      subFilters: combiner.subFilters.map((element) => {
        if ('operator' in element) {
          return this.mapCombiner(element);
        }

        return this.mapOperation(element);
      }),
    };
  }

  protected mapOperation(expression: Expression) {
    return {
      field: expression.field,
      operation: expression.operation,
      value: this.mapValue(expression.value),
    };
  }

  protected getField(name: string, fieldConfig: FilterConfiguration[]): FilterConfiguration | null {
    return fieldConfig.find((f) => f.filterKey === name) || null;
  }

  protected mapValue(value: Expression['value']): string {
    if (isMoment(value)) {
      return DateUtils.toISOString(value);
    }

    return value.toString();
  }
}
